import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, List, Modal, Select, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { API_ITEMS_URL } from '../../utils';
import { formatLookupElementValue } from '../Input';
import { createLookupResolver } from '../../utils/lookups';
import { getItemDisplayValue } from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useLinkedItems from '../../hooks/useLinkedItems';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { drivenSourceLabel, effectiveOrderBy, elementLabel, entryRefOf, groupNamed } from '../../utils/drivenSummary';
import { columnsOf } from '../../config/repeatingGroups';
import { entryNumberWords } from '../../utils/repeatingGroupEntries';

/**
 * "Where did this number come from?" for one driven element on one item.
 *
 * A driven value is denormalized onto the target item, so the record that
 * produced it is invisible from the form — this modal shows it. For `latest`
 * that is the winning contributor (with a picker over the rest, because "why is
 * it that date and not the one I just entered?" is answered by seeing the
 * others); for `count`, `sum`, `min`, `max` and `average` it is the list of
 * everything aggregated.
 *
 * Contributors are read from the ordinary items endpoint in its "source
 * records" mode (`driven_item` + `driven_element`, KPR-768): the SERVER asks the
 * rule's own question — the link, the element's filter and window — so the list
 * is what the number counts, and it asks it under the caller's ACLs, so this
 * modal never sees more than the user does. (It used to fetch the source card's
 * default page of 50 and filter it here: past fifty source records the linked
 * ones were rarely among them, and the filter and window were never applied.)
 * A reader who can see fewer records than the number counts is told how many
 * are hidden, never which.
 *
 * ACROSS A GROUP'S ENTRIES (`linkElementName` is `<group>.<column>`, KPR-1055)
 * the number counts ENTRIES and the list answers the RECORDS that hold them, so
 * the two cannot be subtracted, and the modal says the value counts entries.
 * Since KPR-1106 the API also answers, for such a rule, two headers (CORS
 * exposes both):
 *   - `X-Driven-Entry-Count`, the entries in the records this reader can read.
 *     For a `count` the stored number less it is how many entries sit in
 *     records hidden from the reader, said as a number and never a record
 *     (KPR-1121). For any other aggregate the number is not a count: no note.
 *   - `X-Driven-Source-Order: latest`, the page is in the rule's own order,
 *     winner first, even for an order the list cannot sort by (a column of the
 *     group). Only then is row one marked as the current value.
 * An API older than that sends neither, and the modal is what KPR-1098 left:
 * no note, and no winner marked for a latest ordered by a column of the group
 * rather than a wrong one. A header that is missing, empty or not a whole
 * number is "not sent", never 0.
 *
 * Fetching happens on OPEN, never on render: a form full of driven rows would
 * otherwise fire a collection-wide item query per row on every mount.
 */

const SYSTEM_SOURCE_LABELS = { $createdAt: 'Created', $updatedAt: 'Updated' };
// `latest` is ordered by the server, newest first, so the winner is row one.
const SYSTEM_SORT_FIELDS = { $createdAt: 'createdAt', $updatedAt: 'updatedAt' };
// The API's largest page. More contributors than this are counted, not listed.
const PAGE_SIZE = 200;

// A header the API sent as a whole number, else null. A missing, empty or
// malformed header is "not sent" and must never read as 0: `Number('')` and
// `Number(null)` are 0, which would turn an old API's silence into "every entry
// is hidden from you".
const wholeNumberHeader = ( headers, name ) => {
	const raw = headers?.[ name ];
	if ( typeof raw !== 'string' && typeof raw !== 'number' ) return null;
	const text = String( raw ).trim();
	if ( ! /^\d+$/.test( text ) ) return null;
	const number = Number( text );
	return Number.isSafeInteger( number ) ? number : null;
};

// Does the stored value say there ARE source records? An empty aggregate is ''
// (min, max, average, latest) or 0 (count, sum): neither is a record.
const holdsRecords = ( value, aggregate ) => {
	if ( value === undefined || value === null || value === '' ) return false;
	if ( aggregate === 'count' || aggregate === 'sum' ) return Number( value ) !== 0;
	return true;
};

export default function DrivenSourceModal( { open, onClose, element, item, cardMeta } ) {

	const navigate = useNavigate();
	// Optional: the item form mounts inside the provider, but this modal is
	// rendered from a component that also renders standalone in tests.
	const cardDefinitions = useOptionalCardDefinitions();

	const drivenFrom = element?.options?.drivenFrom || {};
	const sourceCardId = normalizeId( drivenFrom.sourceCardId );
	const collectionId = normalizeId( item?.collection_id || item?.collectionId );
	const targetItemId = normalizeId( item?._id );
	const aggregate = drivenFrom.aggregate || 'latest';
	// The source card's elements, to read the group a link names and the type of
	// the date a `latest` is ordered by.
	const sourceElementDefs = useMemo( () => (
		( sourceCardId && cardDefinitions?.getElementsForCard )
			? ( cardDefinitions.getElementsForCard( sourceCardId ) || [] )
			: []
	), [ cardDefinitions, sourceCardId ] );
	// A link through a repeating group's entries: `<group>.<column>`.
	const entryLink = entryRefOf( drivenFrom.linkElementName );
	// What the API orders a `latest` by: the stored order, else the source
	// itself when it is a date (a group's date column included), else creation.
	const orderBy = effectiveOrderBy( drivenFrom, sourceElementDefs );
	// Every aggregate but `latest` reads ALL contributors alike (count, sum,
	// min, max, average): the list is the explanation, no single winner. A
	// `latest` ordered by a column of a group's entries cannot be asked as a
	// sort (the list cannot sort by a dotted name), so the read sends none, and a
	// winner is marked only when the API says the page came in the rule's own
	// order (`serverOrdered`, KPR-1121). When the source card's elements are not
	// to hand, the order the API would use for an unstored one cannot be told
	// here, so it is left to the API the same way.
	const orderUnknown = !! entryLink && aggregate === 'latest' && ! drivenFrom.orderBy && ! groupNamed( sourceElementDefs, entryLink.group );
	const askedUnordered = aggregate !== 'latest' || !! entryRefOf( orderBy ) || orderUnknown;
	const hasEntryLink = !! entryLink;
	const elementName = element?.name;
	const storedValue = elementName ? item?.elements?.[ elementName ] : undefined;

	const [ sourceItems, setSourceItems ] = useState( [] );
	// How many records the caller can read that the rule counts (X-Total-Count):
	// the list may hold one page of them.
	const [ visibleTotal, setVisibleTotal ] = useState( 0 );
	// Across entries (KPR-1106): the entries of the listed records that link here,
	// and whether the page is in the rule's own order, winner first. Both come
	// from headers an older API does not send: null and false then.
	const [ entryCount, setEntryCount ] = useState( null );
	const [ serverOrdered, setServerOrdered ] = useState( false );
	const isCount = askedUnordered && ! serverOrdered;
	const [ loading, setLoading ] = useState( false );
	// A failed read is its own state with Retry: it must not read as "no source records".
	const [ loadError, setLoadError ] = useState( false );
	const [ attempt, setAttempt ] = useState( 0 );
	// Until a read has answered the body is a skeleton, so "no source records" cannot show first.
	const [ loaded, setLoaded ] = useState( false );
	const [ selectedId, setSelectedId ] = useState( null );

	// A `latest` with no stored order is asked in the order the API derives from
	// the source card's elements (the dialog leaves the default unsaved), so that
	// read waits for the card cache rather than asking twice, once in the wrong
	// order. Every other read has what it needs already.
	const cardsPending = aggregate === 'latest' && ! drivenFrom.orderBy && !! cardDefinitions?.loading;

	useEffect( () => {
		if ( ! open || cardsPending ) return undefined;
		if ( ! targetItemId || ! elementName ) {
			setSourceItems( [] );
			setVisibleTotal( 0 );
			setEntryCount( null );
			setServerOrdered( false );
			setLoaded( true );
			return undefined;
		}
		let active = true;
		setEntryCount( null );
		setServerOrdered( false );
		setLoaded( false );
		setLoadError( false );
		setLoading( true );
		// The server asks the rule's own question (link, filter, window; the
		// source card's subtree) under the caller's read scope. `latest` is
		// asked newest first by the rule's ordering, so its winner is row one.
		api.get( API_ITEMS_URL, {
			params: {
				driven_item: targetItemId,
				driven_element: elementName,
				limit: PAGE_SIZE,
				...( askedUnordered ? {} : { sort_field: SYSTEM_SORT_FIELDS[ orderBy ] || orderBy, sort_direction: 'desc' } ),
			},
		} ).then( res => {
			if ( ! active ) return;
			const rows = Array.isArray( res?.data ) ? res.data : [];
			const header = Number( res?.headers?.[ 'x-total-count' ] );
			// Only a rule linked through a group's entries is answered these two.
			// The order header says the page is in the rule's order: it is the
			// server's word for a latest, so it marks a winner, nothing else does.
			const order = res?.headers?.[ 'x-driven-source-order' ];
			setEntryCount( hasEntryLink ? wholeNumberHeader( res?.headers, 'x-driven-entry-count' ) : null );
			setServerOrdered( hasEntryLink && aggregate === 'latest' && typeof order === 'string' && order.trim().toLowerCase() === 'latest' );
			setSourceItems( rows );
			setVisibleTotal( Number.isFinite( header ) && header >= rows.length ? header : rows.length );
			setLoaded( true );
		} ).catch( () => {
			if ( active ) { setSourceItems( [] ); setVisibleTotal( 0 ); setEntryCount( null ); setServerOrdered( false ); setLoadError( true ); }
		} ).finally( () => {
			if ( active ) setLoading( false );
		} );
		return () => { active = false; };
	}, [ open, cardsPending, targetItemId, elementName, askedUnordered, hasEntryLink, aggregate, orderBy, attempt ] );

	const contributors = sourceItems;
	const winner = isCount ? null : ( contributors[ 0 ] || null );
	const winnerId = normalizeId( winner?._id );

	// The count includes records this reader cannot see (a filtered grant): say
	// how many, never which. Only a count has a number to subtract from.
	// Across entries the stored number counts entries and the list is records,
	// so the records cannot be subtracted: the API's entry count is (KPR-1121),
	// and without it (an older API) there is no note, as KPR-1098 left it.
	const hiddenCount = ( aggregate === 'count' && ! entryLink && Number.isFinite( Number( storedValue ) ) )
		? Math.max( 0, Number( storedValue ) - visibleTotal )
		: 0;
	const storedNumber = Number( storedValue );
	const hiddenEntries = ( aggregate === 'count' && entryLink && entryCount !== null && Number.isSafeInteger( storedNumber ) )
		? Math.max( 0, storedNumber - entryCount )
		: 0;

	// Reopening starts from the answer the row is showing: the winner for
	// `latest`, the list itself for `count` (no row is "the" record there).
	useEffect( () => {
		if ( ! open ) return;
		setSelectedId( isCount ? null : ( winnerId || null ) );
	}, [ open, isCount, winnerId ] );

	const cardsById = cardDefinitions?.cardsById;

	// The contributors alone are not enough of a pool: their card-lookup
	// elements point at items of OTHER cards (a first-aid record's staff
	// lookup points at a staff item), and a resolver over just the fetched
	// source items would fall back to raw ids for all of them — including
	// the Name line whenever the source card's title templates a lookup.
	// useLinkedItems batch-fetches everything referenced (nested included,
	// ACL-filtered by the ?ids= endpoint; unreadable ids keep rendering as
	// raw ids, which is the correct don't-leak fallback).
	const itemsById = useLinkedItems( {
		items: sourceItems,
		getElementsForCard: cardDefinitions?.getElementsForCard,
		enabled: open && !! cardDefinitions?.getElementsForCard,
		resetKey: `${collectionId}:${targetItemId}:${element?.name}`,
	} );

	const resolveLookupValue = useMemo( () => (
		cardDefinitions?.getElementsForCard
			? createLookupResolver( { itemsById, cardsById, getElementsForCard: cardDefinitions.getElementsForCard } )
			: undefined
	), [ cardDefinitions, itemsById, cardsById ] );

	const labelForItem = useCallback( ( entry ) => getItemDisplayValue( {
		card: cardsById?.[ normalizeId( entry?.card_id ) ],
		elementDefs: ( cardDefinitions?.getElementsForCard && entry?.card_id )
			? ( cardDefinitions.getElementsForCard( entry.card_id ) || [] )
			: sourceElementDefs,
		item: entry,
		resolveLookupValue,
		fallback: normalizeId( entry?._id ) || 'Untitled item',
	} ), [ cardDefinitions, cardsById, resolveLookupValue, sourceElementDefs ] );

	const selected = useMemo(
		() => contributors.find( entry => normalizeId( entry?._id ) === selectedId ) || null,
		[ contributors, selectedId ]
	);

	const sourceCardName = ( sourceCardId && cardsById?.[ sourceCardId ]?.name ) || 'source';
	const sourceElementLabel = SYSTEM_SOURCE_LABELS[ drivenFrom.sourceElementName ]
		|| drivenSourceLabel( drivenFrom.sourceElementName, sourceElementDefs );
	const groupLabel = entryLink ? ( elementLabel( groupNamed( sourceElementDefs, entryLink.group ) ) || entryLink.group ) : '';

	const goToItem = ( entry ) => {
		const id = normalizeId( entry?._id );
		const entryCollectionId = normalizeId( entry?.collection_id ) || collectionId;
		if ( ! id || ! entryCollectionId ) return;
		onClose?.();
		navigate( `/collections/${entryCollectionId}/items/${id}` );
	};
	const handleGoToItem = () => goToItem( selected );

	// A record's repeating group is a list of entries, never one value (it used to
	// print as raw JSON). The group the rule reads shows the entries that name
	// this item, each by its other columns (they are what the number is made of);
	// any other group says how many entries it holds.
	const holdsTarget = ( value ) => ( Array.isArray( value ) ? value : [ value ] ).some( id => normalizeId( id ) === targetItemId );
	const renderValue = ( def, entry ) => {
		const value = entry?.elements?.[ def?.name ];
		// Any other group (and any other element) is read as the detail window
		// reads it: a count of entries, in words (KPR-1108).
		if ( def?.dataType !== 'repeating-group' || ! entryLink || def.name !== entryLink.group ) return formatLookupElementValue( def, value, resolveLookupValue );
		// A section heading is an entry with no elements of its own.
		const entries = ( Array.isArray( value ) ? value : [] ).filter( row => row && row.elements );
		if ( ! entries.length ) return '—';
		// Another item lookup in the entry is not batch-resolved here (only an
		// element of the record is), so it would read as an id: left out.
		const columns = columnsOf( def ).filter( column => column?.name && column.name !== entryLink.column && column.dataType !== 'card-lookup' );
		const here = entries.filter( row => holdsTarget( row.elements[ entryLink.column ] ) );
		if ( ! here.length ) return '—';
		return here.map( ( row, index ) => {
			const parts = columns
				.map( column => [ column, formatLookupElementValue( column, row.elements[ column.name ], resolveLookupValue ) ] )
				.filter( ( [ , shown ] ) => shown !== '—' )
				.map( ( [ column, shown ] ) => `${elementLabel( column )}: ${shown}` );
			return <div key={row.id || index}>{parts.join( ' · ' ) || 'An entry with no other values'}</div>;
		} );
	};

	const renderDetail = ( entry ) => (
		<div className='card-lookup-detail-modal__content'>
			<div className='card-lookup-detail-modal__actions'>
				<Button type='primary' onClick={handleGoToItem}>Go to item</Button>
				{ ( ! isCount && normalizeId( entry?._id ) === winnerId ) ? (
					<Tag color='green'>Current value</Tag>
				) : null }
			</div>
			<div className='card-lookup-detail-modal__meta'>
				<div><strong>Name:</strong> {labelForItem( entry )}</div>
				<div><strong>Item ID:</strong> {normalizeId( entry?._id )}</div>
				<div><strong>Card type:</strong> {cardsById?.[ normalizeId( entry?.card_id ) ]?.name || sourceCardName}</div>
			</div>
			<div className='card-lookup-detail-modal__elements'>
				{ sourceElementDefs.length ? sourceElementDefs.map( def => {
					const key = def?.name || def?._id;
					return (
						<div key={key} className='card-lookup-detail-modal__element'>
							<div className='card-lookup-detail-modal__element-label'>{def?.label?.singular || def?.name || 'Field'}</div>
							<div className='card-lookup-detail-modal__element-value'>
								{renderValue( def, entry )}
							</div>
						</div>
					);
				} ) : <Empty description='No elements configured for this card.' /> }
			</div>
		</div>
	);

	const contributorOptions = contributors.map( entry => {
		const id = normalizeId( entry?._id );
		return {
			value: id,
			label: id === winnerId ? `${labelForItem( entry )} — current value` : labelForItem( entry ),
		};
	} );

	// A number and never a record, link or title: how much of the total is out of reach.
	let hiddenNote = null;
	if ( hiddenCount > 0 ) {
		hiddenNote = (
			<Typography.Paragraph type='secondary' style={{ marginBottom: 0 }}>
				{hiddenCount} {hiddenCount === 1 ? 'record' : 'records'} counted here {hiddenCount === 1 ? 'is' : 'are'} hidden from you.
			</Typography.Paragraph>
		);
	} else if ( hiddenEntries > 0 ) {
		hiddenNote = (
			<Typography.Paragraph type='secondary' style={{ marginBottom: 0 }}>
				{entryNumberWords( hiddenEntries )} counted here {hiddenEntries === 1 ? 'is in a record' : 'are in records'} hidden from you.
			</Typography.Paragraph>
		);
	}

	const renderBody = () => {
		if ( ! contributors.length ) {
			// Nothing counted: say so plainly. The old sentence is for the one case
			// it is true of — the number counts records this reader cannot see. It
			// says how many when it can (a count), and never names or links them.
			if ( ! holdsRecords( storedValue, aggregate ) ) return <Empty description='No linked records.' />;
			return (
				<Empty description='No source records are visible to you.'>
					{hiddenNote}
				</Empty>
			);
		}

		if ( isCount ) {
			if ( selected ) {
				return (
					<>
						<div style={{ marginBottom: 12 }}>
							<Button type='link' style={{ paddingInline: 0 }} onClick={() => setSelectedId( null )}>
								Back to all records
							</Button>
						</div>
						{renderDetail( selected )}
					</>
				);
			}
			return (
				<>
					<Typography.Title level={5} style={{ marginTop: 0 }}>
						{visibleTotal} contributing record{visibleTotal === 1 ? '' : 's'}
					</Typography.Title>
					{ ( aggregate === 'latest' ) ? (
						<Typography.Paragraph type='secondary'>
							The current value is the latest across the entries of these records, so no single record is marked.
						</Typography.Paragraph>
					) : null }
					<List
						dataSource={contributors}
						rowKey={( entry, index ) => normalizeId( entry?._id ) || index}
						renderItem={entry => {
							const id = normalizeId( entry?._id );
							return (
								<List.Item
									actions={[
										<Button type='link' key='view' onClick={() => setSelectedId( id )}>View</Button>,
										<Button type='link' key='go' onClick={() => goToItem( entry )}>Go to item</Button>,
									]}
								>
									<List.Item.Meta title={labelForItem( entry )} description={`ID: ${id || '—'}`} />
								</List.Item>
							);
						}}
					/>
					{ visibleTotal > contributors.length ? (
						<Typography.Paragraph type='secondary'>
							Showing the first {contributors.length} of {visibleTotal}.
						</Typography.Paragraph>
					) : null }
					{hiddenNote}
				</>
			);
		}

		return (
			<>
				{ contributors.length > 1 ? (
					<div style={{ marginBottom: 16 }}>
						<Select
							size='middle'
							style={{ width: '100%' }}
							value={selectedId ?? undefined}
							options={contributorOptions}
							onChange={setSelectedId}
							placeholder='Choose a source record to inspect'
							aria-label='Source record'
						/>
					</div>
				) : null }
				{ selected ? renderDetail( selected ) : <Empty description='Select a source record to inspect.' /> }
			</>
		);
	};

	let intro;
	if ( entryLink && aggregate === 'count' ) {
		intro = `Counting the ${groupLabel} entries on ${sourceCardName} records that name this item. A record is listed once, however many of its entries count.`;
	} else if ( entryLink && aggregate !== 'latest' ) {
		intro = `Reading ${sourceElementLabel} from the ${groupLabel} entries on ${sourceCardName} records that name this item. A record is listed once, however many of its entries count.`;
	} else if ( entryLink ) {
		intro = `Driven from ${sourceCardName} · ${sourceElementLabel}, read from the ${groupLabel} entries that name this item.`;
	} else {
		intro = aggregate !== 'latest'
			? `Counting ${sourceCardName} records linked to this item.`
			: `Driven from ${sourceCardName} · ${sourceElementLabel}.`;
	}

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={720}
			centered
			destroyOnClose
			title={`Source records for ${element?.label?.singular || cardMeta?.name || 'this field'}`}
		>
			<Typography.Paragraph type='secondary' style={{ marginTop: 0 }}>
				{ intro }
			</Typography.Paragraph>
			<DialogLoadState
				loaded={loaded}
				loading={loading}
				error={loadError}
				label='the source records'
				onRetry={() => setAttempt( n => n + 1 )}
				skeleton={<Skeleton variant='list-row' count={4} />}
				minHeight={200}
			>
				{renderBody()}
			</DialogLoadState>
		</Modal>
	);

}
